// The Policies tab's checks, read from the JSON schema the backend serves
// (app/modules/projects/policies.py) so a new setting needs no code here:
// whole numbers within minimum/maximum (required unless the schema allows
// null), list items matching the item pattern, at most maxItems. The one
// area-wide rule set the schema can't express — sharing's defaults must be
// among the allowed values — is mirrored by name. The server checks all of it
// again (400 invalid_policy {errors: [{key, type, limit?}]}).

import i18n from '@/i18n';

const t = (key, params) => i18n.t(`errors:fields.${key}`, params);

const numberSchema = (schema) => (schema.type === 'integer' ? schema : (schema.anyOf || []).find((s) => s.type === 'integer'));
const nullable = (schema) => (schema.anyOf || []).some((s) => s.type === 'null');

export function policyValueProblem(schema, value) {
  const number = numberSchema(schema);
  if (number) {
    if (value === null || value === undefined || value === '') return nullable(schema) ? null : t('required');
    if (!Number.isInteger(value)) return t('integer');
    const { minimum: min, maximum: max } = number;
    if (min !== undefined && max !== undefined && (value < min || value > max)) return t('range', { min, max });
    if (min !== undefined && value < min) return t('atLeast', { min });
    if (max !== undefined && value > max) return t('atMost', { max });
    return null;
  }
  if (schema.type === 'array') {
    const items = value || [];
    if (schema.maxItems !== undefined && items.length > schema.maxItems) return t('tooMany', { max: schema.maxItems });
    const pattern = schema.items?.pattern ? new RegExp(schema.items.pattern) : null;
    if (pattern) {
      const bad = items.find((item) => !pattern.test(item));
      if (bad !== undefined) return `${bad}: ${i18n.t('errors:fields.types.string_pattern_mismatch')}`;
    }
  }
  return null;
}

const typeText = (type) => i18n.t(`errors:fields.types.${type}`);

// Sharing: a default must be one of the allowed values.
function sharingProblems(draft) {
  const out = {};
  if (Array.isArray(draft.levels_allowed)) {
    if (!draft.levels_allowed.length) out.levels_allowed = typeText('levels_empty');
    else if (draft.default_level && !draft.levels_allowed.includes(draft.default_level)) out.default_level = typeText('default_level_not_allowed');
  }
  if (Array.isArray(draft.reaches_allowed)) {
    if (!draft.reaches_allowed.length) out.reaches_allowed = typeText('reaches_empty');
    else if (draft.default_reach && !draft.reaches_allowed.includes(draft.default_reach)) out.default_reach = typeText('default_reach_not_allowed');
  }
  if (draft.allow_download_off === false && draft.default_download === false) out.default_download = typeText('download_off_not_allowed');
  return out;
}

export function areaProblems(properties, draft) {
  const out = {};
  Object.entries(properties || {}).forEach(([key, schema]) => {
    const problem = policyValueProblem(schema, draft[key]);
    if (problem) out[key] = problem;
  });
  return { ...sharingProblems(draft), ...out };
}

// The server's `invalid_policy` errors, by setting.
export function policyServerErrors(errorInfo) {
  if (errorInfo?.code !== 'invalid_policy') return null;
  const out = {};
  (errorInfo.params?.errors || []).forEach(({ key, type, limit }) => {
    const field = String(key || '').split('.')[0];
    if (!field || out[field]) return;
    if (limit !== undefined && (type === 'less_than_equal' || type === 'less_than')) out[field] = t('atMost', { max: limit });
    else if (limit !== undefined && (type === 'greater_than_equal' || type === 'greater_than')) out[field] = t('atLeast', { min: limit });
    else if (i18n.exists(`errors:fields.types.${type}`)) out[field] = typeText(type);
    else out[field] = t('invalid');
  });
  return out;
}
